import { Typography } from '@material-ui/core';
import Portal from '@material-ui/core/Portal';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { theme } from 'src/styles/theme';

import CloseIcon from '../../images/close.svg';

const Left = styled.div`
  display: flex;
  align-items: center;
`;

// These two discs read the Ripple variable rather than the theme. A
// styled-components template literal is evaluated at module scope, where no
// hook reaches and where recruiter-ui's bridge — which rewrites values on the
// theme its provider supplies — never arrives, so a theme read here renders
// the FDS value whatever the ramp does. The hex fallback covers a render
// before Ripple's stylesheet loads.
const DISC_TINT = {
  blue: 'var(--ds-color-brand-50, #eef2ff)',
  red: 'var(--ds-color-rose-100, #ffe4e6)',
};

const StyledHeaderIcon = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: ${props => DISC_TINT[props.color] || props.color};
`;

export const HeaderIcon = ({ Icon, color }) => (
  <StyledHeaderIcon color={color}>
    {typeof Icon === 'function' ? (
      <Icon color={color === 'red' && theme.palette.error.main} />
    ) : (
      <img alt="modal-icon" role="presentation" src={Icon} />
    )}
  </StyledHeaderIcon>
);

HeaderIcon.propTypes = {
  Icon: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  color: PropTypes.string,
};

const StyledCloseIcon = styled.button.attrs({
  children: <img alt="close-button" src={CloseIcon} />,
})`
  padding: 10px;
  border: none;
  background: none;
  cursor: pointer;
`;

export const ModalTitle = ({ children, id }) => (
  <Typography
    display="inline"
    id={id}
    style={{ color: theme.palette.common.gray800, marginRight: '10px' }}
    variant="subtitle2"
  >
    {children}
  </Typography>
);

ModalTitle.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  id: PropTypes.string,
};

ModalTitle.defaultProps = {
  id: null,
};

const StyledModalHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: ${theme.customBorders.gray200};
  padding: ${theme.misc.marginMobile}px ${theme.misc.marginDesktop}px;
`;

export const ModalHeader = ({ children, onClose }) => (
  <StyledModalHeader>
    <Left>{children}</Left>
    <StyledCloseIcon onClick={onClose} />
  </StyledModalHeader>
);

ModalHeader.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  onClose: PropTypes.func,
};

export const InnerContent = styled.div`
  padding: ${theme.misc.marginDesktop}px;
  overflow-y: auto;
  ${({ height }) =>
    height &&
    css`
      height: ${height};
    `}
`;

const ModalFooterContainer = styled.div`
  background-color: ${theme.palette.common.gray100};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding: 16px ${theme.misc.marginDesktop}px;
`;

export const ModalFooter = ({ children }) => <ModalFooterContainer>{children}</ModalFooterContainer>;

ModalFooter.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

const Overlay = styled.div`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: ${props => props.blurOverlay && 'blur(6px)'};
  background-color: ${props => (props.blurOverlay ? `rgb(19, 30, 64, 0.6)` : 'rgba(0, 0, 0, 0.5)')};
  z-index: ${theme.zIndex.modal};
  padding: ${props => !props.fullScreenOnMobile && `${theme.misc.marginMobile}px`};
`;

const Modal = styled.div`
  position: relative;
  margin: auto;
  display: flex;
  flex-direction: column;
  max-height: calc(100% - 64px);
  width: ${props =>
    props.maxWidth ? `${typeof props.maxWidth === 'string' ? props.maxWidth : `${props.maxWidth}px`}` : '636px'};
  max-width: 100%;
  z-index: ${theme.zIndex.modal};
  background-color: ${theme.palette.common.white};
  border: ${theme.customBorders.gray200};
  border-radius: ${theme.misc.borderRadius}px;

  ${({ height }) =>
    height &&
    css`
      height: ${height};
      max-height: ${height};
    `}

  @media (max-width: ${theme.misc.mobileBreakpoint}px) {
  ${StyledModalHeader}, ${ModalFooterContainer} {
    padding: ${theme.spacing(1.5, 3)};
  }
  ${ModalFooterContainer} {
    justify-content: ${props => props.fullScreenOnMobile && 'center'};
  }
    ${InnerContent} {
      flex: 1;
      padding: ${theme.spacing(3)}px;
    }
    ${props =>
      props.fullScreenOnMobile &&
      css`
        margin: 0;
        max-height: none;
        height: 100%;
        border-radius: 0;

        ${StyledHeaderIcon} {
          display: none;
        }
      `}
`;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * SimpleModal component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Modal content
 * @param {function} props.onClose - Callback when modal closes
 * @param {boolean} [props.fullScreenOnMobile=true] - Full screen on mobile
 * @param {number} [props.maxWidth] - Max width in pixels
 * @param {string} [props.height] - Fixed height
 * @param {boolean} [props.blurOverlay=false] - Blur background
 * @param {string|null} [props.ariaLabelledBy=null] - ID of element that labels the modal
 */
function SimpleModal({
  children,
  onClose,
  fullScreenOnMobile = true,
  maxWidth,
  height = null,
  blurOverlay,
  ariaLabelledBy = null,
}) {
  const modalRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Callback ref instead of a mount effect: fires the instant the Portal
  // attaches the node to the DOM, so it isn't racing the Portal's own effect
  // timing the way a useEffect on this component would.
  const setModalRef = useCallback(node => {
    modalRef.current = node;
    if (node && !previouslyFocusedRef.current) {
      previouslyFocusedRef.current = document.activeElement;
      const firstFocusable = node.querySelector(FOCUSABLE_SELECTOR);
      (firstFocusable || node).focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        onClose();
        return;
      }

      // Trap Tab focus within the modal so keyboard users can't tab out to
      // the page behind it while it's open.
      if (event.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  // Restore focus to whatever had it before the modal opened.
  useEffect(() => {
    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleKeyDown]);

  return (
    <Portal>
      <Overlay blurOverlay={blurOverlay} fullScreenOnMobile={fullScreenOnMobile} onClick={onClose}>
        <Modal
          ref={setModalRef}
          aria-labelledby={ariaLabelledBy}
          aria-modal="true"
          role="dialog"
          tabIndex={-1}
          fullScreenOnMobile={fullScreenOnMobile}
          height={height}
          maxWidth={maxWidth}
          onClick={e => e.stopPropagation()}
        >
          {children}
        </Modal>
      </Overlay>
    </Portal>
  );
}

SimpleModal.propTypes = {
  ariaLabelledBy: PropTypes.string, // must match id on modal title
  blurOverlay: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  fullScreenOnMobile: PropTypes.bool,
  height: PropTypes.string,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
};

export default styled(SimpleModal)``;
